/**
 * «Revisar y mandar», el último paso del asistente (29/10/2026), en una sola pantalla: arriba de todo «Vas a mandar», lo
 * que se va a mandar de una pasada; después la prueba (el bloque `prueba`), el horario y la baja para repasar, lo que
 * frenó el confirmar y el chequeo entero (quiénes quedan afuera, el plan por día y el costo). «Ahora» o «Programar» se
 * elige en «Cuándo» y acá se repasa en «Vas a mandar»; si se llegó sin elegir (una copia llega directo), se elige acá
 * mismo, debajo de la tarjeta (`elegirCuando`). El chequeo, su vigencia y el confirmar viven en `useConfirmarCampana`.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, OctagonAlert } from "lucide-react";
import type { AudienciaActiva, CampanaDetalle, CampoDisponible, Chequeo, HorarioCampana, PlantillaDetalle, Previa } from "../../types";
import type { PasoAsistente } from "../../logic/chequeo";
import type { EleccionBaja } from "../../logic/baja";
import { audienciaEnPalabras, pideRefuerzo } from "../../logic/vasAMandar";
import { mediaConVariables } from "../../logic/plantilla";
import { BLOQUEO, tituloBloqueo } from "../../logic/textos";
import { destinoParaMostrar } from "../../logic/formato";
import { fechaHoraEnZona } from "../../logic/zonas";
import { NOMBRE_DEL_PASO } from "../../logic/tuCampana";
import type { RechazoConfirmar } from "../../hooks/asistente/useConfirmarCampana";
import { VasAMandar } from "../VasAMandar";
import { AvisoBaja } from "../AvisoBaja";
import { ResumenChequeo } from "../ResumenChequeo";
import { PlanTandas } from "../PlanTandas";
import { CostoEstimado } from "../CostoEstimado";
import { PreviaWhatsApp } from "../PreviaWhatsApp";
import { textoDeLaBaja, textoDelHorario } from "./PasoCuando";

const B = "/activa/oficial";
/** Los links y los botones de texto de los avisos: 44 px de alto, que se tocan bien con el dedo. */
const enlace = "inline-flex min-h-[44px] items-center font-semibold underline";

export function PasoConfirmar({
  campana, detalle, audiencias, chequeo, calculando, errorChequeo, errorChequeoPaso, chequeoViejo, onRecalcular, avisoDesactualizado, rechazo,
  onConfirmarDeNuevo, onAveriguar, averiguando, cuando, elegirCuando, sinChequeo, onUsarHora, prueba, franjas, hoy, previa, previaConfirmar, onReintentarPrevia, cantidadEscrita, onCantidadEscrita,
  horario, franjasMarca, eleccionBaja, botonBaja, onCambiarHorario, fatigaDias, fatigaMaximo, recargoPct, campos, onIrAPaso,
}: {
  campana: CampanaDetalle | null;
  /** la plantilla elegida: su nombre y categoría en «Vas a mandar», y el aviso de una promoción sin baja */
  detalle?: PlantillaDetalle;
  /** los grupos de la campaña de Activa, para decir a quiénes en palabras */
  audiencias: AudienciaActiva[] | null;
  chequeo: Chequeo | null; calculando: boolean; errorChequeo: string; chequeoViejo: boolean;
  /** el error del chequeo se arregla en ese paso (un público borrado, una variable sin dato): lleva ahí en vez de reintentar */
  errorChequeoPaso: PasoAsistente | null;
  /** «Reintentar» y «Recalcular» el chequeo */
  onRecalcular: () => void;
  avisoDesactualizado: boolean; rechazo: RechazoConfirmar | null;
  /** sin respuesta del confirmar: «Reintentar» (sigue en borrador) y «Volver a fijarme» (no se supo cómo quedó) */
  onConfirmarDeNuevo: () => void; onAveriguar: () => void; averiguando: boolean;
  /** lo elegido en «Cuándo» (null = todavía nada), la fecha programada (ISO) y la zona de la campaña */
  cuando: { modo: "ahora" | "programar" | null; iso: string | null; zona: string };
  /** «Ahora» o «Programar» para elegir acá (`ElegirCuando`), si se llegó sin elegirlo; si no, null */
  elegirCuando?: ReactNode;
  /** por qué todavía no se pide el chequeo (falta la fecha, el horario tiene un error): se arregla en «Cuándo» */
  sinChequeo: string;
  /** programa el envío para la próxima apertura de la franja */
  onUsarHora: (iso: string) => void;
  /** la prueba: mandarla, cómo salió y «Seguir sin probar» (`PasoPrueba`) */
  prueba: ReactNode;
  /** la franja en palabras («09:00 a 21:00 (lun a vie)») */
  franjas: string; hoy: Date;
  /** la previa real: la de Variables o la que se pide al entrar (`previaConfirmar`) */
  previa: Previa | null; previaConfirmar: "" | "pidiendo" | "error"; onReintentarPrevia: () => void;
  /** la cantidad de personas, escrita: la confirmación reforzada de un envío grande */
  cantidadEscrita: string; onCantidadEscrita: (s: string) => void;
  /** lo elegido en «Cuándo», para repasarlo */
  horario: HorarioCampana | null; franjasMarca: string; eleccionBaja: EleccionBaja | ""; botonBaja?: string | null;
  onCambiarHorario: () => void;
  fatigaDias: number; fatigaMaximo: number; recargoPct?: number; campos: CampoDisponible[]; onIrAPaso: (p: PasoAsistente) => void;
}) {
  const idCampana = campana?.id || "";
  const verDetalleDelChequeo = () => document.getElementById("detalle-del-chequeo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const ejemploPrevia = previa?.ejemplos?.[0] ?? null;
  return (
    <div className="space-y-5">
      {/* Arriba de todo, lo que se va a mandar de una pasada (29/10/2026). */}
      {chequeo && (
        <VasAMandar
          plantilla={detalle ? { nombre: detalle.nombreVisible, categoria: detalle.categoria } : campana?.plantilla ? { nombre: campana.plantilla.nombreVisible, categoria: campana.plantilla.categoria } : null}
          audiencia={audienciaEnPalabras(campana, { grupos: audiencias?.find((a) => a.id === campana?.audiencia?.campanaActivaId)?.grupos, chequeo })}
          chequeo={chequeo}
          cuando={cuando}
          franjas={franjas}
          hoy={hoy}
          previa={ejemploPrevia && previa ? (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">
                Así le llega a {ejemploPrevia.nombre || "una persona de la audiencia"} · <span className="font-mono">{destinoParaMostrar(ejemploPrevia.destino, false)}</span>
              </p>
              <PreviaWhatsApp compacto texto={ejemploPrevia.texto} mediaUrl={mediaConVariables(previa.plantilla.mediaUrl, ejemploPrevia.variables)} botones={previa.plantilla.botones || []}
                pie={previa.plantilla.pie} encabezadoTexto={previa.plantilla.encabezadoTexto} />
            </div>
          ) : null}
          sinPrevia={previaConfirmar === "pidiendo" ? (
            <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Armando la previa…</p>
          ) : previaConfirmar === "error" ? (
            <p className="text-sm text-slate-500">
              No pudimos armar la previa. <button type="button" onClick={onReintentarPrevia} className={`${enlace} text-violet-700`}>Reintentar</button>
            </p>
          ) : (
            <p className="text-sm text-slate-500">No hay contactos para mostrar de ejemplo: miralo en «Qué les decís».</p>
          )}
          onVerDetalle={verDetalleDelChequeo}
          refuerzo={{ pide: pideRefuerzo(chequeo), escrito: cantidadEscrita, onEscrito: onCantidadEscrita }}
        />
      )}
      {elegirCuando && (
        <section aria-label="Cuándo sale" data-testid="cuando-en-revisar"
          className={`rounded-2xl border bg-white p-5 shadow-sm ${cuando.modo === null ? "border-amber-200" : "border-slate-100"}`}>
          {elegirCuando}
        </section>
      )}
      <section aria-labelledby="titulo-prueba" className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm space-y-3">
        <h4 id="titulo-prueba" className="text-base font-semibold text-slate-900 tracking-tight">La prueba</h4>
        {prueba}
      </section>
      <AvisoBaja plantilla={detalle} />

      {/* El horario y la baja se eligen en «Cuándo»; acá se repasan. */}
      <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="resumen-horario-baja">
        <div className="space-y-0.5">
          <p><span className="font-semibold">Horario:</span> {textoDelHorario(horario, chequeo?.franjas || null, franjasMarca)}</p>
          <p><span className="font-semibold">Baja:</span> {eleccionBaja ? textoDeLaBaja(eleccionBaja, botonBaja) : "falta elegirla"}</p>
        </div>
        <button type="button" onClick={onCambiarHorario} className={`${enlace} text-violet-700 whitespace-nowrap`}>Cambiar</button>
      </div>

      {avisoDesactualizado && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          Los números cambiaron desde que los calculaste (alguien se dio de baja, cambió el saldo o el cupo). Revisalos y confirmá de nuevo.
        </p>
      )}
      {chequeo?.enFranjaCuando === false && cuando.modo === "programar" && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span>Esa hora está fuera de la franja.{chequeo.proximaApertura && ` Se abre el ${fechaHoraEnZona(chequeo.proximaApertura, cuando.zona)}.`}</span>
          {chequeo.proximaApertura && (
            <button type="button" onClick={() => onUsarHora(chequeo.proximaApertura as string)} className={enlace}>Usar esa hora</button>
          )}
        </div>
      )}
      {rechazo && (
        <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700 space-y-1.5" role="alert">
          <p className="flex items-center gap-2 font-medium"><OctagonAlert size={14} /> {rechazo.texto}</p>
          {rechazo.bloqueos && (
            <ul className="list-disc pl-6">
              {rechazo.bloqueos.map((b) => <li key={b}>{tituloBloqueo(b, { faltanteUSD: chequeo?.saldo?.faltanteUSD, comprometidoUSD: chequeo?.saldo?.comprometidoUSD, gruposEnOtraCampana: chequeo?.gruposEnOtraCampana })}{BLOQUEO[b]?.accion?.ruta && <> · <Link to={BLOQUEO[b].accion?.ruta as string} className="inline-flex min-h-[44px] items-center underline">{BLOQUEO[b].accion?.label}</Link></>}</li>)}
            </ul>
          )}
          {rechazo.proximaApertura && (
            <button type="button" onClick={() => onUsarHora(rechazo.proximaApertura as string)} className={enlace}>
              Programar para el {fechaHoraEnZona(rechazo.proximaApertura, cuando.zona)}
            </button>
          )}
          {rechazo.paso && (
            <button type="button" onClick={() => onIrAPaso(rechazo.paso as PasoAsistente)} className={enlace}>Ir a «{NOMBRE_DEL_PASO[rechazo.paso]}»</button>
          )}
          {rechazo.linkDetalle && <Link to={`${B}/campanas/${encodeURIComponent(idCampana)}`} className={enlace}>Ver la campaña</Link>}
          {rechazo.reintentar && (
            <button type="button" onClick={onConfirmarDeNuevo} className={enlace}>Reintentar</button>
          )}
          {rechazo.averiguar && (
            <button type="button" onClick={onAveriguar} disabled={averiguando} className={`${enlace} disabled:opacity-50`}>
              {averiguando ? "Fijándonos…" : "Volver a fijarme"}
            </button>
          )}
        </div>
      )}
      {errorChequeo && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {errorChequeoPaso ? (
            <>
              <span>{errorChequeo}</span>
              <button type="button" onClick={() => onIrAPaso(errorChequeoPaso)} className={`${enlace} whitespace-nowrap`}>Ir a «{NOMBRE_DEL_PASO[errorChequeoPaso]}»</button>
            </>
          ) : (
            <>
              <span>No pudimos calcular el chequeo. {errorChequeo}</span>
              <button type="button" onClick={onRecalcular} className={enlace}>Reintentar</button>
            </>
          )}
        </div>
      )}
      {chequeoViejo && !calculando && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span>El cálculo tiene más de 10 minutos: puede haber cambiado algo.</span>
          <button type="button" onClick={onRecalcular} className={enlace}>Recalcular</button>
        </div>
      )}

      {!chequeo ? (
        sinChequeo ? (
          <p className="flex flex-wrap items-center gap-x-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="sin-chequeo">
            {sinChequeo}
            <button type="button" onClick={onCambiarHorario} className={enlace}>Cambiarlo en «Cuándo»</button>
          </p>
        ) : calculando || !errorChequeo ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Calculando…</div>
        ) : null
      ) : (
        <div className={`space-y-5 ${calculando ? "opacity-60" : ""}`} aria-busy={calculando}>
          {calculando && <p className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" /> Calculando…</p>}
          <div id="detalle-del-chequeo" className="scroll-mt-4" />
          <ResumenChequeo
            chequeo={chequeo}
            fatigaDias={fatigaDias}
            fatigaMaximo={fatigaMaximo}
            onIrAPaso={onIrAPaso}
            mapeo={campana?.mapeo}
            campos={campos}
            programado={cuando.modo === "programar"}
          />
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <h4 className="text-base font-semibold text-slate-900 tracking-tight mb-3">Plan de envío</h4>
            <PlanTandas plan={chequeo.plan} cupo24h={chequeo.cupo24h} zona={cuando.zona} />
          </div>
          <CostoEstimado
            costo={chequeo.costo}
            saldo={chequeo.saldo}
            tope={chequeo.tope}
            recargoPct={recargoPct}
            envio={chequeo.envio}
            audienciaBase={campana?.audiencia?.tipo === "base"}
            programadaPara={cuando.iso}
          />
        </div>
      )}
    </div>
  );
}
